import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, ActivityIndicator, RefreshControl } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { apiService, VisitCampaign, VisitCampaignStatus } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';

const STATUS_FILTERS: Array<VisitCampaignStatus | 'all'> = ['all', 'draft', 'active', 'paused', 'completed', 'cancelled'];

const statusColor = (status: VisitCampaignStatus): string => {
  switch (status) {
    case 'active':
      return colors.status.success as string;
    case 'completed':
      return colors.status.info as string;
    case 'cancelled':
      return colors.status.error as string;
    case 'paused':
      return colors.status.warning as string;
    default:
      return colors.text.secondary as string;
  }
};

const statusIcon = (status: VisitCampaignStatus): keyof typeof Ionicons.glyphMap => {
  switch (status) {
    case 'active': return 'play-circle';
    case 'completed': return 'checkmark-circle';
    case 'cancelled': return 'ban';
    case 'paused': return 'pause-circle';
    default: return 'document-outline';
  }
};

export const VisitCampaignsList: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo } = useAppContext();
  const { t } = useLocale();

  const [campaigns, setCampaigns] = useState<VisitCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Defaults to 'active' — confirmed real usability gap: this list opened
  // showing every campaign ever created (draft/completed/cancelled all
  // mixed in with the ones actually running), same fix as VisitsList's own
  // active-only default.
  const [statusFilter, setStatusFilter] = useState<VisitCampaignStatus | 'all'>('active');

  const loadCampaigns = useCallback(async (isRefresh: boolean) => {
    if (isRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    try {
      const params: Record<string, any> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await apiService.listVisitCampaigns(params);
      setCampaigns(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn('[VisitCampaignsList] loadCampaigns error:', err);
      setCampaigns([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadCampaigns(false);
  }, [loadCampaigns]);

  const statusLabel = (status: VisitCampaignStatus | 'all'): string => {
    if (status === 'all') return t('visits.filters.all');
    return t(`visitCampaigns.status.${status}` as any);
  };

  const formatDateRange = (campaign: VisitCampaign): string => {
    try {
      const start = new Date(campaign.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const end = new Date(campaign.endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      return `${start} - ${end}`;
    } catch {
      return '';
    }
  };

  const renderCard = ({ item }: { item: VisitCampaign }) => (
    <TouchableOpacity
      style={styles.card}
      activeOpacity={0.7}
      onPress={() => navigateTo('visit-campaign-detail', undefined, { campaignId: item._id })}
    >
      <View style={styles.cardTop}>
        <Text style={styles.cardName} numberOfLines={1}>{item.name}</Text>
        <View style={[styles.statusBadge, { borderColor: statusColor(item.status) }]}>
          <Ionicons name={statusIcon(item.status)} size={11} color={statusColor(item.status)} />
          <Text style={[styles.statusBadgeText, { color: statusColor(item.status) }]}>{statusLabel(item.status)}</Text>
        </View>
      </View>
      {item.description ? (
        <Text style={styles.cardDescription} numberOfLines={2}>{item.description}</Text>
      ) : null}
      <View style={styles.cardBottom}>
        <View style={styles.cardMetaItem}>
          <Ionicons name="calendar-outline" size={14} color={colors.text.secondary as string} />
          <Text style={styles.cardMetaText}>{formatDateRange(item)}</Text>
        </View>
        <View style={styles.cardMetaItem}>
          <Ionicons name="people-outline" size={14} color={colors.text.secondary as string} />
          <Text style={styles.cardMetaText}>{t('visitCampaigns.list.contactCount', { count: item.contactCount })}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <FlatList
        horizontal
        showsHorizontalScrollIndicator={false}
        data={STATUS_FILTERS}
        keyExtractor={(s) => s}
        style={styles.filterRow}
        contentContainerStyle={styles.filterRowContent}
        renderItem={({ item }) => {
          const active = item === statusFilter;
          return (
            <TouchableOpacity
              style={[styles.filterChip, active && styles.filterChipActive]}
              onPress={() => setStatusFilter(item)}
            >
              <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>{statusLabel(item)}</Text>
            </TouchableOpacity>
          );
        }}
      />

      {isLoading ? (
        <View style={styles.centered}>
          <ActivityIndicator color={colors.primary as string} />
        </View>
      ) : (
        <FlatList
          data={campaigns}
          keyExtractor={(c) => c._id}
          renderItem={renderCard}
          contentContainerStyle={campaigns.length === 0 ? styles.emptyListContent : styles.listContent}
          refreshControl={
            <RefreshControl refreshing={isRefreshing} onRefresh={() => loadCampaigns(true)} tintColor={colors.primary as string} />
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <Ionicons name="briefcase-outline" size={40} color={colors.text.secondary as string} />
              <Text style={styles.emptyStateText}>{t('visitCampaigns.list.empty')}</Text>
            </View>
          }
        />
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },

  filterRow: { flexGrow: 0, marginBottom: spacing.sm },
  filterRowContent: { paddingHorizontal: spacing.lg, gap: spacing.xs },
  filterChip: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 16,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    marginRight: spacing.xs,
  },
  filterChipActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },
  filterChipText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  filterChipTextActive: { color: colors.white as string, fontFamily: typography.fontFamily.semibold },

  listContent: { paddingHorizontal: spacing.lg, paddingBottom: spacing.xl },
  emptyListContent: { flexGrow: 1, paddingHorizontal: spacing.lg },

  card: {
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border as string,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  cardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs },
  cardName: { flex: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string, marginRight: spacing.sm },
  cardDescription: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginBottom: spacing.xs },
  statusBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderRadius: 12, paddingVertical: 2, paddingHorizontal: spacing.sm },
  statusBadgeText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },
  cardBottom: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginTop: spacing.xs },
  cardMetaItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  cardMetaText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },

  emptyState: { alignItems: 'center', justifyContent: 'center', paddingVertical: spacing.xl * 2, gap: spacing.sm },
  emptyStateText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
});
